import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, ShieldOff, Printer, Send, RefreshCw } from "lucide-react";
import useConsentTexts from "@/hooks/useConsentTexts";
import { CONSENT_LABELS as SCOPE } from "@/components/ConsentCheckboxes";
import LegalLayout from "./LegalLayout";
import { EMAIL } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
const STATUS = {
  active: { label: "Actif", cls: "bg-emerald-50 text-emerald-700" },
  expired: { label: "Expiré", cls: "bg-slate-100 text-slate-600" },
  withdrawn: { label: "Retiré", cls: "bg-red-50 text-red-700" },
  renewed: { label: "Renouvelé", cls: "bg-blue-50 text-blue-700" },
};

export default function ConsentManage() {
  const { token } = useParams();
  return (
    <LegalLayout eyebrow="Vos droits" title="Gérer mon consentement" updated="" testId="consent-page">
      {token ? <ConsentProof token={token} /> : <WithdrawByEmail />}
    </LegalLayout>
  );
}

function ConsentProof({ token }) {
  const { state } = useLocation();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/consent/${token}`).then((r) => (r.ok ? r.json() : Promise.reject())).then((d) => setDoc({ ...d, token })).catch(() => setError(true));
  }, [token]);

  const withdraw = async () => {
    if (!window.confirm("Confirmer le retrait de votre consentement ?")) return;
    setBusy(true);
    const r = await fetch(`${API}/consent/${token}/withdraw`, { method: "POST" });
    if (r.ok) setDoc({ ...(await r.json()), token });
    setBusy(false);
  };

  if (error) return <div data-testid="consent-not-found" className="space-y-6"><p className="text-md-muted">Ce lien n'est pas valide ou a expiré. Vous pouvez retirer votre consentement avec votre adresse email :</p><WithdrawByEmail /></div>;
  if (!doc) return <p className="text-md-muted">Chargement…</p>;
  const st = STATUS[doc.status];

  return (
    <div className="bg-white border border-md-border rounded-3xl p-8 md:p-10 space-y-6" data-testid="consent-proof">
      {state?.renewed && doc.status === "active" && (
        <div data-testid="consent-renewed-success" className="flex items-center gap-3 bg-emerald-50 text-emerald-800 rounded-2xl px-5 py-4 text-sm print:hidden">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" /> Merci, votre accord est renouvelé pour 12 mois. Conservez ce lien pour le gérer à tout moment.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl text-md-primary">Preuve de consentement</h2>
        <span data-testid="consent-status" className={`text-xs uppercase font-bold tracking-widest px-3 py-1 rounded-full ${st.cls}`}>{st.label}</span>
      </div>
      <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
        <Item k="Titulaire" v={`${doc.full_name} — ${doc.email}`} />
        <Item k="Formulaire" v={doc.source === "devis" ? "Demande de devis" : "Formulaire de contact"} />
        <Item k="Recueilli le" v={fmt(doc.created_at)} />
        <Item k="Valable jusqu'au" v={fmt(doc.expires_at)} />
        <Item k="Adresse IP" v={doc.ip || "—"} />
        <Item k="Version des conditions" v={doc.version} />
        {doc.withdrawn_at && <Item k="Retiré le" v={fmt(doc.withdrawn_at)} />}
        <Item k="Appelant" v={doc.caller} />
      </dl>
      <div className="space-y-3">
        {doc.scopes.map((s) => (
          <div key={s.type} className="bg-md-bg rounded-2xl p-4 text-sm">
            <div className="text-[10px] uppercase tracking-widest text-md-secondary font-bold mb-1">{SCOPE[s.type]}</div>
            <p className="text-md-text/90">« {s.text} »</p>
          </div>
        ))}
      </div>
      {["active", "expired"].includes(doc.status) && <RenewBox doc={doc} token={doc.token} />}
      {doc.status === "renewed" && <p data-testid="consent-renewed-note" className="text-sm text-md-muted">Cet accord a été renouvelé : utilisez le lien reçu après le renouvellement pour gérer votre nouvel accord.</p>}
      <div className="flex flex-wrap gap-3 pt-2 print:hidden">
        {doc.status === "active" && (
          <button data-testid="consent-withdraw-btn" disabled={busy} onClick={withdraw} className="inline-flex items-center gap-2 bg-md-primary hover:bg-md-primaryHover disabled:opacity-50 text-white px-6 py-3 rounded-full text-sm font-semibold transition-colors">
            <ShieldOff className="w-4 h-4" /> Retirer mon consentement
          </button>
        )}
        <button data-testid="consent-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 border border-md-border hover:border-md-primary text-md-primary px-6 py-3 rounded-full text-sm font-semibold transition-colors">
          <Printer className="w-4 h-4" /> Imprimer / enregistrer en PDF
        </button>
      </div>
    </div>
  );
}

function RenewBox({ doc, token }) {
  const cfg = useConsentTexts();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!cfg) return null;

  const renew = async () => {
    setBusy(true);
    const r = await fetch(`${API}/consent/${token}/renew`, { method: "POST" });
    setBusy(false);
    if (!r.ok) return setErr("Le renouvellement n'a pas pu être enregistré.");
    const n = await r.json();
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/consentement/${n.token}`, { replace: true, state: { renewed: true } });
  };

  return (
    <div data-testid="consent-renew-box" className="border-2 border-md-secondary/40 bg-md-secondary/5 rounded-2xl p-6 space-y-4 print:hidden">
      <div className="font-serif text-xl text-md-primary">Renouveler mon accord pour 12 mois</div>
      {doc.scopes.map((s) => <p key={s.type} className="text-sm text-md-text/90">« {cfg.texts[doc.source][s.type]} »</p>)}
      <p className="text-xs text-md-muted">Appelant : {cfg.caller}. Une nouvelle preuve sera enregistrée à la date d'aujourd'hui.</p>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <button data-testid="consent-renew-btn" disabled={busy} onClick={renew} className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover disabled:opacity-50 text-white px-6 py-3 rounded-full text-sm font-semibold transition-colors">
        <RefreshCw className="w-4 h-4" /> {busy ? "Enregistrement…" : "Je renouvelle mon accord"}
      </button>
    </div>
  );
}

const Item = ({ k, v }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-widest text-md-muted mb-1">{k}</dt>
    <dd className="text-md-text break-words">{v}</dd>
  </div>
);

function WithdrawByEmail() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await fetch(`${API}/consent/withdraw`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
    setBusy(false);
    if (r.ok) setDone(true);
  };

  if (done) {
    return (
      <div data-testid="consent-withdraw-success" className="bg-white border border-md-border rounded-3xl p-8 flex gap-4 items-start">
        <ShieldCheck className="w-6 h-6 text-md-accent flex-shrink-0" />
        <p className="text-md-text">C'est fait. Si des consentements étaient associés à <strong>{email}</strong>, ils sont désormais retirés et vous ne serez plus démarché(e).</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} data-testid="consent-withdraw-form" className="bg-white border border-md-border rounded-3xl p-8 md:p-10 space-y-5">
      <p className="text-md-text/90 leading-relaxed">
        Indiquez l'adresse email utilisée lors de votre demande : tous les consentements au recontact et à la prospection associés seront retirés immédiatement et gratuitement. Pour obtenir la preuve de votre consentement, utilisez le lien reçu par email ou écrivez à {EMAIL}.
      </p>
      <input data-testid="consent-withdraw-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jean@email.com" className="w-full bg-md-bg border border-md-border rounded-xl p-4 focus:outline-none focus:border-md-secondary" />
      <button data-testid="consent-withdraw-submit" disabled={busy} className="inline-flex items-center gap-2 bg-md-primary hover:bg-md-primaryHover disabled:opacity-50 text-white px-6 py-3 rounded-full text-sm font-semibold transition-colors">
        <Send className="w-4 h-4" /> {busy ? "Envoi…" : "Retirer mon consentement"}
      </button>
    </form>
  );
}
